import { ReactNode, useState, useEffect } from 'react';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { TpDataContext } from '../../../../data/interfaces';
import styles from '../SingleItemView.module.scss';
import { SectionIndex, TpSectionsIndexContext } from '../../../../components/sections-index/SectionsIndex';
import { MarkDownLinkRenderer } from '../../../../components/markdown/MarkDownLinkRenderer';
import { TpSingleEntityViewContext } from '../interfaces';
import { getDataTableByCanonicalType } from '../../charts/chartDataBindings';
import Markdown from 'react-markdown';
import { entityIsCompatible, formatDataTableStringTemplate } from './utils';
import { PresignedBlobData } from '../../../../api/models/data';
import { useApiContext } from '../../../../api';
import { Spinner } from '../../../../components/primitives/Spinner';
import { MissingImage } from '../../../../components/primitives/ErrorMessages';

const ATTACHMENT_TYPE = 'attachment';

interface TpEntityImageProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate?: SingleEntityTemplate;
  elementTemplate: any;
  children?: ReactNode;
  sectionIndexContext: TpSectionsIndexContext;
  singleEntityViewContext: TpSingleEntityViewContext;
}

interface TpElementTemplate {
  title: string;
  sectionId: string;
  description?: string;
  fileType: string; // "attachment" or an extension like "png", "jpg", "pdf", ...
  url: string;
  requiredProperty?: string;
  requiredPropertyValues?: any[];
  height?: number;
}

/**
 * A component to render an image for the entity. The image can come from an external URL
 * or from an attachment stored in the platform.
 * In case the image is an attachment, a pre-signed URL will be generated to access the image securely.
 * While the image is loading, a placeholder will be shown.
 */
const EntityImage = (props: TpEntityImageProps) => {
  const { itemId } = props;
  const elementTemplate = props.elementTemplate as TpElementTemplate;
  const canonicalTypeId = props.entityTypeTemplate!.entityType;
  const dataTable = getDataTableByCanonicalType(props.dataContext, canonicalTypeId);
  const baseUrl = formatDataTableStringTemplate(elementTemplate.url, dataTable, itemId);
  const shouldDisplay = entityIsCompatible(
    dataTable,
    itemId,
    elementTemplate.requiredProperty,
    elementTemplate.requiredPropertyValues,
  );

  const api = useApiContext();

  // State management for URL resolution
  const [actualUrl, setActualUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolveUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // If the image is an attachment, generate a pre-signed URL
        if (elementTemplate.fileType === ATTACHMENT_TYPE) {
          const blobData: PresignedBlobData = {
            method: 'GET',
            blobPath: baseUrl,
            isDownload: false, // Set to false for inline viewing
          };

          const response = await api.blob.getPresignedURL(blobData);

          if (response.signedUrl) {
            setActualUrl(response.signedUrl);
          } else {
            setError('Failed to generate presigned URL for attachment');
          }
        } else {
          // For external URLs, use the URL directly
          setActualUrl(baseUrl);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load image');
      } finally {
        setIsLoading(false);
      }
    };

    if (shouldDisplay) {
      resolveUrl();
    }
  }, [baseUrl, elementTemplate.fileType, api, shouldDisplay, elementTemplate]);

  if (!shouldDisplay) {
    return null;
  }

  return (
    <SectionIndex
      sectionIndexContext={props.sectionIndexContext}
      sectionId={`image_${elementTemplate.sectionId}`}
      sectionName={elementTemplate.title}
    >
      <div className={styles.tableTitle}>{elementTemplate.title}</div>

      {elementTemplate.description && (
        <div className={styles.sectionDescription}>
          <Markdown components={{ a: MarkDownLinkRenderer }}>
            {formatDataTableStringTemplate(elementTemplate.description, dataTable, itemId)}
          </Markdown>
        </div>
      )}

      <div className={styles.imgContainer}>
        {isLoading && <Spinner />}
        {error && <MissingImage message={error} />}
        {!isLoading && !error && actualUrl && (
          <img
            style={{ height: elementTemplate.height !== undefined ? `${elementTemplate.height}px` : 'auto' }}
            src={actualUrl}
            alt={`${elementTemplate.title}`}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setError('Failed to load image')}
          />
        )}
      </div>
    </SectionIndex>
  );
};

export default EntityImage;
